import React, {
 ChangeEvent,
 DragEvent,
 useRef,
 useState,
} from 'react'
import {useTranslation} from 'react-i18next'
import {KTSVG} from 'helpers'
import {generateRandomString} from 'utils/helper'

type IProps = {
 existingDocumentUrl?: string
 pendingDocumentUrl?: string
 pendingFileName?: string
 pendingFileSize?: number
 onFileSelected: (file: File) => void
 onDeleteDocument: () => void
 disabled?: boolean
}

const formatFileSize = (bytes?: number) => {
 if (bytes === undefined || bytes === null || bytes <= 0) {
  return null
 }
 const kb = Math.max(1, Math.round(bytes / 1024))
 return `${kb} kb`
}

const getDisplayNameFromUrl = (url?: string) => {
 if (!url) return 'Document'
 try {
  const pathname = new URL(url, window.location.origin)
   .pathname
  const raw = pathname.split('/').pop() || 'Document'
  return decodeURIComponent(raw).split('?')[0] || 'Document'
 } catch {
  const raw = url.split('/').pop() || 'Document'
  return decodeURIComponent(raw).split('?')[0] || 'Document'
 }
}

const isImageUrl = (url?: string) => {
 if (!url) return false
 return /\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i.test(url)
}

const PaymentTermsUpload = (props: IProps) => {
 const {t} = useTranslation()
 const [isDragging, setIsDragging] = useState(false)
 const fileInputId = useRef(
  `payment-terms-${generateRandomString(5)}`
 ).current

 const acceptFile = (file?: File) => {
  if (!file || props.disabled) return
  props.onFileSelected(file)
 }

 const handleFileChange = (
  e: ChangeEvent<HTMLInputElement>
 ) => {
  acceptFile(e.target.files?.[0])
  e.target.value = ''
 }

 const handleDragEnter = (e: DragEvent<HTMLElement>) => {
  e.preventDefault()
  e.stopPropagation()
  if (!props.disabled) setIsDragging(true)
 }

 const handleDragOver = (e: DragEvent<HTMLElement>) => {
  e.preventDefault()
  e.stopPropagation()
  if (!props.disabled) setIsDragging(true)
 }

 const handleDragLeave = (e: DragEvent<HTMLElement>) => {
  e.preventDefault()
  e.stopPropagation()
  setIsDragging(false)
 }

 const handleDrop = (e: DragEvent<HTMLElement>) => {
  e.preventDefault()
  e.stopPropagation()
  setIsDragging(false)
  if (props.disabled) return
  acceptFile(e.dataTransfer.files?.[0])
 }

 const dropzoneHandlers = props.disabled
  ? {}
  : {
     onDragEnter: handleDragEnter,
     onDragOver: handleDragOver,
     onDragLeave: handleDragLeave,
     onDrop: handleDrop,
    }

 const documentUrl =
  props.pendingDocumentUrl || props.existingDocumentUrl
 const hasUploadedDocument = Boolean(documentUrl)
 const fileName =
  props.pendingFileName ||
  getDisplayNameFromUrl(documentUrl)
 const fileSizeLabel = formatFileSize(props.pendingFileSize)
 const showImageThumb = isImageUrl(documentUrl)

 const openDocument = () => {
  if (!documentUrl) return
  window.open(documentUrl, '_blank', 'noopener,noreferrer')
 }

 const handleCardClick = () => {
  if (props.disabled) return
  openDocument()
 }

 const stopEventPropagation = (
  e: React.MouseEvent<HTMLElement>
 ) => {
  e.stopPropagation()
 }

 const handleCardKeyDown = (
  e: React.KeyboardEvent<HTMLDivElement>
 ) => {
  if (props.disabled) return
  if (e.key === 'Enter' || e.key === ' ') {
   e.preventDefault()
   openDocument()
  }
 }

 const actionBtnClass =
  'inline-flex items-center justify-center w-9 h-9 rounded-md border border-[#D7DFEA] bg-white text-[#5A6066] cursor-pointer shrink-0 disabled:opacity-50 disabled:cursor-not-allowed'

 return (
  <div className='w-full'>
   <div className='text-[#33393D] text-lg font-bold mb-4'>
    {t(
     'myAccountPage.businessProfile.offtaker.paymentTerms.title'
    )}
   </div>

   {hasUploadedDocument ? (
    <div
     className={`group flex items-center justify-between gap-4 w-full min-h-[96px] rounded-lg border px-5 py-4 transition-all duration-200 ${
      isDragging
       ? 'border-[#4E83E4] bg-[#EAF2FF]'
       : props.disabled
       ? 'border-[#D7DFEA] bg-[#F5F8FA] opacity-50 cursor-not-allowed'
       : 'cursor-pointer border-[#D7DFEA] bg-[#F5F8FA] hover:border-[#B8C6D8] hover:bg-[#EEF3F8] hover:shadow-[0_4px_14px_rgba(23,27,31,0.08)]'
     }`}
     onClick={handleCardClick}
     onKeyDown={handleCardKeyDown}
     role='button'
     tabIndex={props.disabled ? -1 : 0}
     aria-label='Open uploaded document'
     {...dropzoneHandlers}
    >
     <div className='flex items-center gap-3 min-w-0'>
      <div className='relative w-12 h-12 rounded-md overflow-hidden bg-white border border-[#E6EBF0] flex items-center justify-center shrink-0'>
       {showImageThumb ? (
        <img
         src={documentUrl}
         alt={fileName}
         className='w-full h-full object-cover'
        />
       ) : (
        <i
         className='fa fa-file-pdf-o text-[#1C88ED] text-xl'
         aria-hidden='true'
        />
       )}
       {!props.disabled && (
        <div className='pointer-events-none absolute inset-0 flex items-center justify-center bg-[rgba(23,27,31,0.45)] opacity-0 transition-opacity duration-200 group-hover:opacity-100'>
         <i
          className='fa fa-arrows-alt text-white text-base'
          aria-hidden='true'
         />
        </div>
       )}
      </div>
      <div className='flex flex-col min-w-0'>
       <span className='text-[#171B1F] text-sm font-bold truncate'>
        {fileName}
       </span>
       <span className='flex items-center gap-1.5 text-xs mt-0.5'>
        <i
         className='fa fa-check-circle text-[#30835A]'
         aria-hidden='true'
        />
        <span className='text-[#30835A] font-medium'>
         Uploaded
        </span>
        {fileSizeLabel && (
         <>
          <span className='text-[#80868C]'>•</span>
          <span className='text-[#80868C]'>
           {fileSizeLabel}
          </span>
         </>
        )}
       </span>
      </div>
     </div>

     <div
      className='flex items-center gap-2 shrink-0'
      onClick={stopEventPropagation}
     >
      <label
       htmlFor={fileInputId}
       className={`${actionBtnClass} ${
        props.disabled ? 'pointer-events-none' : ''
       }`}
       aria-label='Replace document'
      >
       <KTSVG
        path='/media/icons/uploadIcon.svg'
        svgClassName='w-4 h-4 shrink-0 text-[#5A6066]'
       />
      </label>
      <button
       type='button'
       className={actionBtnClass}
       onClick={props.onDeleteDocument}
       aria-label={t('common.delete')}
       disabled={props.disabled}
      >
       <i
        className='fa fa-trash text-sm'
        aria-hidden='true'
       />
      </button>
     </div>
    </div>
   ) : (
    <label
     htmlFor={fileInputId}
     {...dropzoneHandlers}
     className={`flex items-center gap-3 w-full min-h-[96px] cursor-pointer rounded-lg border border-dashed px-5 py-4 ${
      isDragging
       ? 'border-[#4E83E4] bg-[#EAF2FF]'
       : 'border-[#D7DFEA] bg-[#F5F8FA]'
     } ${
      props.disabled ? 'opacity-50 cursor-not-allowed' : ''
     }`}
    >
     <KTSVG
      path='/media/icons/uploadFile.svg'
      svgClassName='w-5 h-5 shrink-0'
     />
     <div className='flex flex-col'>
      <span className='text-[#171B1F] text-sm font-bold'>
       {t(
        'myAccountPage.businessProfile.offtaker.paymentTerms.clickToUpload'
       )}
      </span>
      <span className='text-[#80868C] text-xs mt-0.5'>
       {t(
        'myAccountPage.businessProfile.offtaker.paymentTerms.supportedFormats'
       )}
      </span>
     </div>
    </label>
   )}

   <input
    id={fileInputId}
    type='file'
    className='hidden'
    accept='.pdf,.jpg,.jpeg,.png'
    disabled={props.disabled}
    onChange={handleFileChange}
   />
   <p className='text-[#80868C] text-xs mt-2'>
    {t(
     'myAccountPage.businessProfile.offtaker.paymentTerms.note'
    )}
   </p>
  </div>
 )
}

export default PaymentTermsUpload
